import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import {
  Sparkles,
  Send,
  Wand2,
  Loader2,
  Trash2,
  RefreshCw,
  Linkedin,
  Hash,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  getLinkedInProfile,
  generateLinkedInDraft,
  analyseLinkedInContent,
  publishLinkedInPost,
  listLinkedInPosts,
  updateLinkedInDraft,
  deleteLinkedInDraft,
  recommendLinkedInHashtags,
} from "@/lib/api/linkedin.functions";
import {
  KIND_LABEL,
  LINKEDIN_CATEGORIES,
  LINKEDIN_TOPICS,
  CTA_STYLE_LABEL,
  type CtaStyle,
  type LinkedInCategory,
  type LinkedInKind,
} from "@/lib/linkedin-topics";

export const Route = createFileRoute("/_authenticated/portal/admin/linkedin-studio")({
  head: () => ({
    meta: [
      { title: "LinkedIn Studio — Admin | NDLCS" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: LinkedInStudioPage,
});

function LinkedInStudioPage() {
  const qc = useQueryClient();
  const profile = useServerFn(getLinkedInProfile);
  const generate = useServerFn(generateLinkedInDraft);
  const analyse = useServerFn(analyseLinkedInContent);
  const publish = useServerFn(publishLinkedInPost);
  const listFn = useServerFn(listLinkedInPosts);
  const updateFn = useServerFn(updateLinkedInDraft);
  const delFn = useServerFn(deleteLinkedInDraft);
  const recommendFn = useServerFn(recommendLinkedInHashtags);

  const profileQ = useQuery({
    queryKey: ["linkedin-profile"],
    queryFn: () => profile(),
    retry: false,
  });

  const postsQ = useQuery({
    queryKey: ["linkedin-posts"],
    queryFn: () => listFn({ data: { status: "all", limit: 60 } }),
  });

  const [category, setCategory] = useState<LinkedInCategory | "All">("All");
  const [kind, setKind] = useState<LinkedInKind>("services");
  const [ctaStyle, setCtaStyle] = useState<CtaStyle>("question");
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("");
  const [pasteText, setPasteText] = useState("");
  const [analysis, setAnalysis] = useState<any>(null);
  const [filterCategory, setFilterCategory] = useState<LinkedInCategory | "All">("All");

  const topicsForCategory = useMemo(
    () =>
      category === "All"
        ? LINKEDIN_TOPICS
        : LINKEDIN_TOPICS.filter((t) => t.category === category),
    [category],
  );

  const genMut = useMutation({
    mutationFn: () => generate({ data: { kind, topic, audience, ctaStyle } }),
    onSuccess: () => {
      toast.success("Draft generated");
      qc.invalidateQueries({ queryKey: ["linkedin-posts"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const analyseMut = useMutation({
    mutationFn: (opts: { text?: string; id?: string; autoApply?: boolean }) =>
      analyse({ data: opts }),
    onSuccess: (r) => {
      setAnalysis(r);
      toast.success(`Analysed — score ${r.score}/100`);
      qc.invalidateQueries({ queryKey: ["linkedin-posts"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const pubMut = useMutation({
    mutationFn: (id: string) => publish({ data: { id } }),
    onSuccess: () => {
      toast.success("Posted to LinkedIn");
      qc.invalidateQueries({ queryKey: ["linkedin-posts"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Publish failed"),
  });

  const updMut = useMutation({
    mutationFn: (v: { id: string; content: string }) => updateFn({ data: v }),
    onSuccess: () => {
      toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["linkedin-posts"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["linkedin-posts"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const recMut = useMutation({
    mutationFn: (v: { id?: string; text?: string; applyToDraft?: boolean }) =>
      recommendFn({ data: v }),
    onSuccess: (r) => {
      toast.success(`Recommended ${r.hashtags.length} hashtags`);
      qc.invalidateQueries({ queryKey: ["linkedin-posts"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const filteredPosts = useMemo(() => {
    const rows = postsQ.data ?? [];
    if (filterCategory === "All") return rows;
    const allowed = new Set(
      LINKEDIN_TOPICS.filter((t) => t.category === filterCategory).map((t) => t.kind),
    );
    return rows.filter((r: any) => allowed.has(r.kind));
  }, [postsQ.data, filterCategory]);

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 md:p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Linkedin className="h-6 w-6 text-[#0A66C2]" /> LinkedIn Studio
          </h1>
          <p className="text-sm text-muted-foreground">
            Draft, improve and publish scroll-stopping LinkedIn posts across every
            niche you serve — with configurable CTAs and auto-recommended hashtags.
          </p>
        </div>
        {profileQ.data && (
          <div className="text-right text-xs text-muted-foreground">
            Connected as{" "}
            <span className="font-medium text-foreground">{profileQ.data.name}</span>
          </div>
        )}
      </div>

      {profileQ.isError && (
        <div className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm">
          Could not reach LinkedIn: {(profileQ.error as Error).message}
        </div>
      )}

      {/* Generate draft */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> Generate a new post
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1">
              <Label>Category</Label>
              <Select
                value={category}
                onValueChange={(v) => setCategory(v as LinkedInCategory | "All")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All categories</SelectItem>
                  {LINKEDIN_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Post type</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as LinkedInKind)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {category === "All"
                    ? LINKEDIN_CATEGORIES.map((cat) => {
                        const items = LINKEDIN_TOPICS.filter((t) => t.category === cat);
                        if (!items.length) return null;
                        return (
                          <SelectGroup key={cat}>
                            <SelectLabel>{cat}</SelectLabel>
                            {items.map((t) => (
                              <SelectItem key={t.kind} value={t.kind}>
                                {t.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        );
                      })
                    : topicsForCategory.map((t) => (
                        <SelectItem key={t.kind} value={t.kind}>
                          {t.label}
                        </SelectItem>
                      ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Comment-bait CTA</Label>
              <Select value={ctaStyle} onValueChange={(v) => setCtaStyle(v as CtaStyle)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(CTA_STYLE_LABEL) as CtaStyle[]).map((s) => (
                    <SelectItem key={s} value={s}>
                      {CTA_STYLE_LABEL[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Topic / angle (optional)</Label>
              <Input
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. Section 189 retrenchments, or LLB graduates in Limpopo"
              />
            </div>
            <div className="space-y-1">
              <Label>Target audience (optional)</Label>
              <Input
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                placeholder="e.g. HR managers at SA SMEs"
              />
            </div>
          </div>
          <Button onClick={() => genMut.mutate()} disabled={genMut.isPending}>
            {genMut.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            Generate draft
          </Button>
        </CardContent>
      </Card>

      {/* Analyse existing content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wand2 className="h-4 w-4" /> Read my LinkedIn & improve
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Paste any existing LinkedIn post. The AI grades it, lists concrete
            improvements, and rewrites it to a higher-scoring version. Use{" "}
            <em>Suggest hashtags</em> to get 10 targeted tags for the pasted text.
          </p>
          <Textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder="Paste your LinkedIn post or About section here…"
            rows={6}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => analyseMut.mutate({ text: pasteText, autoApply: false })}
              disabled={analyseMut.isPending || pasteText.trim().length < 20}
            >
              {analyseMut.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="mr-2 h-4 w-4" />
              )}
              Suggest improvements
            </Button>
            <Button
              onClick={() => analyseMut.mutate({ text: pasteText, autoApply: true })}
              disabled={analyseMut.isPending || pasteText.trim().length < 20}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Auto-apply & save as draft
            </Button>
            <Button
              variant="outline"
              onClick={() => recMut.mutate({ text: pasteText })}
              disabled={recMut.isPending || pasteText.trim().length < 20}
            >
              {recMut.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Hash className="mr-2 h-4 w-4" />
              )}
              Suggest hashtags
            </Button>
          </div>

          {recMut.data && (
            <div className="rounded-md border bg-muted/30 p-3 text-sm">
              <div className="mb-1 font-medium">Recommended hashtags</div>
              <div className="flex flex-wrap gap-1.5">
                {recMut.data.hashtags.map((h) => (
                  <Badge key={h} variant="secondary">#{h}</Badge>
                ))}
              </div>
              {recMut.data.reasoning && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {recMut.data.reasoning}
                </p>
              )}
            </div>
          )}

          {analysis && (
            <div className="space-y-3 rounded-md border bg-muted/30 p-3 text-sm">
              <div>
                <Badge variant="secondary">Score {analysis.score}/100</Badge>
              </div>
              {!!analysis.strengths?.length && (
                <div>
                  <div className="font-medium">Strengths</div>
                  <ul className="ml-5 list-disc">
                    {analysis.strengths.map((s: string, i: number) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {!!analysis.weaknesses?.length && (
                <div>
                  <div className="font-medium">Weaknesses</div>
                  <ul className="ml-5 list-disc">
                    {analysis.weaknesses.map((s: string, i: number) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {!!analysis.improvements?.length && (
                <div>
                  <div className="font-medium">Improvements applied</div>
                  <ul className="ml-5 list-disc">
                    {analysis.improvements.map((s: string, i: number) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <div className="font-medium">Improved version</div>
                <pre className="mt-1 whitespace-pre-wrap rounded bg-background p-2 text-xs">
                  {analysis.improved}
                </pre>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Draft list */}
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle>Your posts</CardTitle>
          <div className="flex items-center gap-2">
            <Select
              value={filterCategory}
              onValueChange={(v) => setFilterCategory(v as LinkedInCategory | "All")}
            >
              <SelectTrigger className="h-8 w-[180px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All categories</SelectItem>
                {LINKEDIN_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="outline"
              onClick={() => qc.invalidateQueries({ queryKey: ["linkedin-posts"] })}
            >
              <RefreshCw className="mr-1 h-3 w-3" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {postsQ.isLoading && (
            <div className="text-sm text-muted-foreground">Loading…</div>
          )}
          {filteredPosts.length === 0 && !postsQ.isLoading && (
            <div className="text-sm text-muted-foreground">
              No posts in this category yet.
            </div>
          )}
          {filteredPosts.map((p: any) => (
            <PostRow
              key={p.id}
              post={p}
              onSave={(content) => updMut.mutate({ id: p.id, content })}
              onPublish={() => pubMut.mutate(p.id)}
              onImprove={() => analyseMut.mutate({ id: p.id, autoApply: true })}
              onRecommend={() => recMut.mutate({ id: p.id, applyToDraft: true })}
              onDelete={() => delMut.mutate(p.id)}
              busy={
                pubMut.isPending ||
                analyseMut.isPending ||
                updMut.isPending ||
                recMut.isPending
              }
            />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function PostRow({
  post,
  onSave,
  onPublish,
  onImprove,
  onRecommend,
  onDelete,
  busy,
}: {
  post: any;
  onSave: (v: string) => void;
  onPublish: () => void;
  onImprove: () => void;
  onRecommend: () => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const [content, setContent] = useState<string>(post.content);
  const dirty = content !== post.content;
  const posted = post.status === "posted";
  return (
    <div className="rounded-md border p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
        <Badge variant={posted ? "default" : "secondary"}>{post.status}</Badge>
        <Badge variant="outline">
          {KIND_LABEL[post.kind as LinkedInKind] ?? post.kind}
        </Badge>
        {post.ai_score != null && (
          <Badge variant="outline">Score {post.ai_score}/100</Badge>
        )}
        {post.topic && (
          <span className="text-muted-foreground">· {post.topic}</span>
        )}
        <span className="ml-auto text-muted-foreground">
          {new Date(post.created_at).toLocaleString()}
        </span>
      </div>
      <Textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={6}
        readOnly={posted}
        className="text-sm"
      />
      {post.ai_notes && (
        <details className="mt-1 text-xs text-muted-foreground">
          <summary>AI notes</summary>
          <pre className="whitespace-pre-wrap">{post.ai_notes}</pre>
        </details>
      )}
      {post.last_error && (
        <div className="mt-1 text-xs text-destructive">
          Last error: {post.last_error}
        </div>
      )}
      {!posted && (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button size="sm" onClick={onPublish} disabled={busy}>
            <Send className="mr-1 h-3 w-3" /> Publish to LinkedIn
          </Button>
          <Button size="sm" variant="outline" onClick={onImprove} disabled={busy}>
            <Wand2 className="mr-1 h-3 w-3" /> Auto-improve
          </Button>
          <Button size="sm" variant="outline" onClick={onRecommend} disabled={busy}>
            <Hash className="mr-1 h-3 w-3" /> Refresh hashtags
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onSave(content)}
            disabled={!dirty || busy}
          >
            Save edits
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive"
            onClick={onDelete}
            disabled={busy}
          >
            <Trash2 className="mr-1 h-3 w-3" /> Delete
          </Button>
        </div>
      )}
      {posted && post.linkedin_urn && (
        <div className="mt-2 text-xs text-muted-foreground">
          Posted · URN {post.linkedin_urn}
        </div>
      )}
    </div>
  );
}
